import React, { useId } from "react";
import "./BrushStrokes.css";

/*
 * Blue and orange paint-brush streaks, the brand mark of the social posts, for the edges of photos and coloured bars.
 * Pure decoration: hidden from assistive tech, never focusable, never takes the pointer.
 * The streaks rise to the right like the slash in the logo.
 *
 * `placement`: an absolutely positioned SVG pinned to an edge of the nearest positioned parent.
 *   top-left      a square: streaks sweep in from the left edge and fray out towards the top
 *   bottom-right  a square: the same drawing turned half a turn, in from the right edge
 *   top-right     the bottom-right drawing pinned to the top-right corner
 *   band          a thin full-width strip of streaks along an edge (under the navbar, over the footer)
 *
 * `variant`: a drawing the parent's class places and sizes (class `brush-variant`, see BrushStrokes.css).
 *   corner  bold blue and orange streaks for the corner of a photo or card (turn it with the class)
 *   swipe   a short accent swipe and a hairline, flicking up under the end of a section title
 *   stroke  a wide orange stroke over a thinner blue one, to set a name on like a poster (stretches to its box)
 *   swash   a flat orange stroke with a blue edge, to set a display title on (stretches to its box)
 *   rule    a long accent stroke and a hairline, to underline a table head (stretches to its box)
 */

export type BrushPlacement = "top-left" | "top-right" | "bottom-right" | "band";
export type BrushVariant = "corner" | "swipe" | "stroke" | "swash" | "rule";

type BrushStrokesProps =
  | {
      placement: BrushPlacement;
      /** Side of the square in px on wide screens (phones get 45% of it, see BrushStrokes.css). Not for the band. */
      size?: number;
      variant?: undefined;
      className?: string;
    }
  | { variant: BrushVariant; placement?: undefined; size?: undefined; className?: string };

type Colour = "blue" | "orange";

// A streak: it starts at (x, y), runs `length` units at `angle` degrees (counter-clockwise from pointing right),
// and is `width` units thick. Starts beyond the box edge hide the streak's head, so only its frayed tail shows.
type Streak = { x: number; y: number; length: number; width: number; colour: Colour; seed: number; angle: number };

// The texture filter, in the drawing's own units: noise that roughens the edges, and long thin grain that opens
// dry-brush gaps (alpha = cut - slope * noise). A stretched band is drawn small, so it gets coarser, fewer gaps.
type Texture = { rough: string; roughScale: number; grain: string; slope: number; cut: number };

type Drawing = {
  width: number;
  height: number;
  streaks: Streak[];
  texture: Texture;
  /** Turned half a turn (the bottom-right and top-right corners) */
  turn?: boolean;
  /** Stretched to the box's shape (the band) */
  stretch?: boolean;
};

const SQUARE_TEXTURE: Texture = { rough: "0.035 0.7", roughScale: 2.8, grain: "0.015 1.2", slope: 9, cut: 5.7 };
const BAND_TEXTURE: Texture = { rough: "0.008 0.22", roughScale: 3.5, grain: "0.004 0.28", slope: 9, cut: 6.1 };

const SLASH = 62;

// For the top-left corner of a 100 x 100 box: in from the left edge.
const CORNER: Streak[] = [
  { x: -8, y: 72, length: 78, width: 15, colour: "blue", seed: 11, angle: SLASH },
  { x: -12, y: 108, length: 84, width: 10, colour: "orange", seed: 23, angle: SLASH },
  { x: -6, y: 34, length: 40, width: 4, colour: "orange", seed: 37, angle: SLASH },
  { x: 6, y: 106, length: 56, width: 3.2, colour: "blue", seed: 41, angle: SLASH },
];

// For a 1000 x 40 strip, stretched to the edge's width: two blue streaks from either end meeting in the middle,
// with thinner orange ones beside them.
const BAND: Streak[] = [
  { x: -20, y: 21, length: 660, width: 12, colour: "blue", seed: 17, angle: 0.6 },
  { x: 1020, y: 16, length: 580, width: 10, colour: "blue", seed: 31, angle: 180.6 },
  { x: 150, y: 33, length: 560, width: 5, colour: "orange", seed: 53, angle: 0.5 },
  { x: 1010, y: 6, length: 320, width: 3.5, colour: "orange", seed: 59, angle: 180.4 },
];

const DRAWINGS: Record<BrushPlacement, Drawing> = {
  "top-left": { width: 100, height: 100, streaks: CORNER, texture: SQUARE_TEXTURE },
  "bottom-right": { width: 100, height: 100, streaks: CORNER, turn: true, texture: SQUARE_TEXTURE },
  "top-right": { width: 100, height: 100, streaks: CORNER, turn: true, texture: SQUARE_TEXTURE },
  band: { width: 1000, height: 40, streaks: BAND, stretch: true, texture: BAND_TEXTURE },
};

// Small deterministic random numbers, so the drawing is the same on every render.
const random = (seed: number) => {
  let state = seed * 9301 + 49297;
  return () => {
    state = (state * 9301 + 49297) % 233280;
    return state / 233280;
  };
};

const round = (n: number) => Math.round(n * 100) / 100;

/**
 * One streak as a bundle of bristle bands laid along +x from 0 to `length`, centred on y = 0.
 * Each band starts and ends a little differently, the outer bands end sooner (a tapering tip),
 * and a few thin hairs and paint specks trail past the tip: the dry-brush look.
 */
const streakShapes = ({ length, width, seed }: Streak) => {
  const rand = random(seed);
  const bands = Math.max(3, Math.round(width));
  const step = width / bands;
  const polygons: string[] = [];
  for (let i = 0; i < bands; i++) {
    const top = -width / 2 + i * step + (rand() < 0.28 ? step * 0.35 : 0);
    const bottom = -width / 2 + (i + 1) * step;
    const middle = (top + bottom) / 2;
    const edge = Math.abs(i + 0.5 - bands / 2) / (bands / 2); // 0 in the middle of the streak, 1 at its sides
    const start = rand() * length * 0.06;
    const end = length * (1 - 0.06 - edge * edge * 0.32 - rand() * 0.16);
    const point = Math.min(length * 0.08, (bottom - top) * 6);
    polygons.push(
      [
        [start, middle],
        [start + point * 0.4, top],
        [end - point, top],
        [end, middle + (rand() - 0.5) * (bottom - top)],
        [end - point * 1.4, bottom],
        [start + point * 0.4, bottom],
      ]
        .map(([x, y]) => `${round(x)},${round(y)}`)
        .join(" ")
    );
  }
  const hairs = Array.from({ length: Math.max(2, Math.round(width / 3)) }, () => {
    const y = (rand() - 0.5) * width * 0.8;
    const from = length * (0.55 + rand() * 0.2);
    const to = length * (0.92 + rand() * 0.14);
    const h = Math.max(0.35, step * (0.25 + rand() * 0.3));
    return { x: round(from), y: round(y - h / 2), width: round(to - from), height: round(h) };
  });
  const specks = Array.from({ length: 3 + Math.round(width / 3) }, () => ({
    cx: round(length * (0.8 + rand() * 0.3)),
    cy: round((rand() - 0.5) * width * 1.6),
    r: round(0.25 + rand() * 0.55),
  }));
  return { polygons, hairs, specks };
};

const RENDERED = Object.fromEntries(
  Object.entries(DRAWINGS).map(([placement, drawing]) => {
    const longest = Math.max(...drawing.streaks.map((s) => s.length));
    const widest = Math.max(...drawing.streaks.map((s) => s.width));
    // The filter works in each streak's own space: room for the specks past the tip and the roughened edges.
    const region = { x: -12, y: -(widest + 6), width: longest * 1.15 + 24, height: (widest + 6) * 2 };
    return [placement, { region, streaks: drawing.streaks.map((streak) => ({ streak, shapes: streakShapes(streak) })) }];
  })
) as Record<BrushPlacement, { region: Record<"x" | "y" | "width" | "height", number>; streaks: { streak: Streak; shapes: ReturnType<typeof streakShapes> }[] }>;

// The variants: flat shapes roughened by one displacement filter (its region and noise per drawing).
// `accent` follows the page's accent (--spark); `second` is the other brand colour beside it.
type VariantShape = {
  colour: Colour | "accent" | "second";
  points?: string;
  rect?: { x: number; y: number; width: number; height: number };
  opacity?: number;
};

type VariantDrawing = {
  viewBox: string;
  /** Stretched to the box's shape */
  stretch?: boolean;
  /** Filter region (fractions of the drawing's box), noise frequency and seed, displacement */
  filter: { x: string; y: string; width: string; height: string; frequency: string; seed: number; scale: number };
  transform?: string;
  shapes: VariantShape[];
};

const VARIANTS: Record<BrushVariant, VariantDrawing> = {
  corner: {
    viewBox: "0 0 260 300",
    filter: { x: "-10%", y: "-40%", width: "120%", height: "180%", frequency: "0.035 0.6", seed: 7, scale: 14 },
    transform: "rotate(-62 60 120)",
    shapes: [
      { colour: "blue", rect: { x: -40, y: 40, width: 300, height: 16 } },
      { colour: "orange", rect: { x: -30, y: 70, width: 240, height: 9 } },
      { colour: "blue", rect: { x: 10, y: 92, width: 280, height: 22 }, opacity: 0.9 },
      { colour: "orange", rect: { x: -60, y: 126, width: 200, height: 7 } },
      { colour: "orange", rect: { x: 40, y: 146, width: 230, height: 12 }, opacity: 0.95 },
    ],
  },
  swipe: {
    viewBox: "0 0 120 40",
    filter: { x: "-10%", y: "-60%", width: "120%", height: "220%", frequency: "0.06 0.9", seed: 3, scale: 7 },
    transform: "rotate(-22 60 20)",
    shapes: [
      { colour: "accent", rect: { x: 8, y: 14, width: 104, height: 8 } },
      { colour: "second", rect: { x: 28, y: 26, width: 80, height: 3.5 } },
    ],
  },
  stroke: {
    viewBox: "0 0 200 40",
    stretch: true,
    filter: { x: "-6%", y: "-40%", width: "112%", height: "180%", frequency: "0.05 0.45", seed: 5, scale: 5 },
    shapes: [
      { colour: "blue", points: "16,30 194,27 200,31 196,37 22,39" },
      { colour: "orange", points: "2,9 184,5 196,9 199,18 193,30 6,32" },
    ],
  },
  swash: {
    viewBox: "0 0 200 40",
    stretch: true,
    filter: { x: "-10%", y: "-40%", width: "120%", height: "180%", frequency: "0.06 0.5", seed: 7, scale: 8 },
    transform: "rotate(-2 100 20)",
    shapes: [
      { colour: "blue", rect: { x: 14, y: 27, width: 182, height: 9 } },
      { colour: "orange", rect: { x: 0, y: 5, width: 192, height: 26 } },
      { colour: "orange", rect: { x: 120, y: 1, width: 66, height: 3 }, opacity: 0.85 },
    ],
  },
  rule: {
    viewBox: "0 0 400 14",
    stretch: true,
    filter: { x: "-2%", y: "-80%", width: "104%", height: "260%", frequency: "0.05 0.5", seed: 9, scale: 4.5 },
    shapes: [
      { colour: "accent", points: "0,2.5 340,1.5 384,2.5 399,5.5 386,9 300,9.5 0,10" },
      { colour: "second", rect: { x: 70, y: 11.2, width: 318, height: 1.8 } },
    ],
  },
};

const VariantBrush: React.FC<{ variant: BrushVariant; filterId: string; className?: string }> = ({
  variant,
  filterId,
  className,
}) => {
  const { viewBox, stretch, filter, transform, shapes } = VARIANTS[variant];
  return (
    <svg
      className={["brush-variant", `brush-variant--${variant}`, className].filter(Boolean).join(" ")}
      viewBox={viewBox}
      preserveAspectRatio={stretch ? "none" : undefined}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <filter id={filterId} x={filter.x} y={filter.y} width={filter.width} height={filter.height}>
          <feTurbulence
            type="fractalNoise"
            baseFrequency={filter.frequency}
            numOctaves="3"
            seed={filter.seed}
            result="noise"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="noise"
            scale={filter.scale}
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </defs>
      <g filter={`url(#${filterId})`} transform={transform}>
        {shapes.map(({ colour, points, rect, opacity }, i) =>
          points ? (
            <polygon key={i} className={`brush-variant__${colour}`} points={points} opacity={opacity} />
          ) : (
            <rect key={i} className={`brush-variant__${colour}`} {...rect} opacity={opacity} />
          )
        )}
      </g>
    </svg>
  );
};

const PlacedBrush: React.FC<{ placement: BrushPlacement; size?: number; filterId: string; className?: string }> = ({
  placement,
  size,
  filterId,
  className,
}) => {
  const drawing = DRAWINGS[placement];
  const { texture } = drawing;
  const { region, streaks } = RENDERED[placement];
  const style = size ? ({ "--brush-size": `${size}px` } as React.CSSProperties) : undefined;
  return (
    <svg
      className={["brush-strokes", `brush-strokes--${placement}`, className].filter(Boolean).join(" ")}
      viewBox={`0 0 ${drawing.width} ${drawing.height}`}
      preserveAspectRatio={drawing.stretch ? "none" : undefined}
      aria-hidden="true"
      focusable="false"
      style={style}
    >
      <defs>
        {/* In each streak's own (rotated) space: stretched noise roughens the edges, then long thin grain
            along the streak opens dry-brush gaps */}
        <filter id={filterId} filterUnits="userSpaceOnUse" {...region}>
          <feTurbulence type="fractalNoise" baseFrequency={texture.rough} numOctaves="2" seed="4" result="noise" />
          <feDisplacementMap
            in="SourceGraphic"
            in2="noise"
            scale={texture.roughScale}
            xChannelSelector="R"
            yChannelSelector="G"
            result="rough"
          />
          <feTurbulence type="fractalNoise" baseFrequency={texture.grain} numOctaves="2" seed="9" result="grain" />
          <feColorMatrix
            in="grain"
            type="matrix"
            values={`0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -${texture.slope} ${texture.cut}`}
            result="holes"
          />
          <feComposite in="rough" in2="holes" operator="in" />
        </filter>
      </defs>
      <g transform={drawing.turn ? `rotate(180 ${drawing.width / 2} ${drawing.height / 2})` : undefined}>
        {streaks.map(({ streak, shapes }) => (
          <g
            key={streak.seed}
            className={`brush-strokes__streak brush-strokes__streak--${streak.colour}`}
            transform={`translate(${streak.x} ${streak.y}) rotate(${-streak.angle})`}
          >
            <g filter={`url(#${filterId})`}>
              {shapes.polygons.map((points, i) => (
                <polygon key={i} points={points} />
              ))}
              {shapes.hairs.map((hair, i) => (
                <rect key={i} {...hair} opacity={0.8} />
              ))}
              {shapes.specks.map((speck, i) => (
                <circle key={i} {...speck} />
              ))}
            </g>
          </g>
        ))}
      </g>
    </svg>
  );
};

const BrushStrokes: React.FC<BrushStrokesProps> = ({ placement, size, variant, className }) => {
  const filterId = `brush-${useId().replace(/[^\w-]/g, "")}`;
  if (variant) return <VariantBrush variant={variant} filterId={filterId} className={className} />;
  return <PlacedBrush placement={placement ?? "top-left"} size={size} filterId={filterId} className={className} />;
};

export default BrushStrokes;
